import React from "react";
import { Box, Typography, Stack } from "@mui/joy";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Plus, X, TrendingUp, Square } from "lucide-react";
import { FormInput, Button, IconButton } from "../../../shared";

interface RoofPitchFieldProps {
    disabled?: boolean;
}

export function RoofPitchField({ disabled }: RoofPitchFieldProps) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "RoofPitch",
    });

    return (
        <Box
            sx={{
                width: "100%",
                bgcolor: "background.surface",
                p: 1.5,
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <Typography
                level="body-sm"
                sx={{
                    textTransform: "uppercase",
                    fontSize: "12px",
                    fontWeight: "xl",
                    letterSpacing: "0.5px",
                    mb: 0.5,
                }}
            >
                Roof Pitches
            </Typography>
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ mt: -0.5, mb: 1.5, fontSize: "12px" }}
            >
                Add multiple roof pitches and their square footage.
            </Typography>

            <Stack spacing={1.5}>
                {fields.map((item, index) => (
                    <Box
                        key={item.id}
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 1, sm: 0 },
                            mb: { xs: 0.5, sm: 0 },
                            pb: { xs: 1.5, sm: 0 },
                            borderBottom: {
                                xs: "1px solid var(--standard-palette-divider)",
                                sm: "none",
                            },
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        {/* Section name + mobile delete */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flex: { sm: 1.5 },
                                width: "100%",
                                minWidth: 0,
                                alignItems: "stretch",
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name={`RoofPitch.${index}.Name`}
                                    placeholder="Section (e.g. Main)"
                                    disabled={disabled}
                                    sx={{
                                        borderTopRightRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderBottomRightRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                    }}
                                />
                            </Box>
                            <IconButton
                                variant="soft"
                                color="danger"
                                disabled={disabled}
                                onClick={() => remove(index)}
                                sx={{
                                    display: { xs: "flex", sm: "none" },
                                    height: "auto",
                                    alignSelf: "stretch",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <X size={16} />
                            </IconButton>
                        </Box>

                        {/* Pitch, Squares & desktop delete */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: { xs: 1, sm: 0 },
                                flex: { sm: 2 },
                                width: "100%",
                                minWidth: 0,
                                alignItems: "flex-start",
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name={`RoofPitch.${index}.Pitch`}
                                    placeholder="Pitch (e.g. 6/12)"
                                    disabled={disabled}
                                    startDecorator={<TrendingUp size={16} />}
                                    sx={{
                                        borderRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderLeft: {
                                            xs: undefined,
                                            sm: 0,
                                        },
                                        minWidth: 0,
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name={`RoofPitch.${index}.Squares`}
                                    type="number"
                                    placeholder="0"
                                    disabled={disabled}
                                    startDecorator={<Square size={16} />}
                                    endDecorator={
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                color: "text.tertiary",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            SF
                                        </Typography>
                                    }
                                    slotProps={{
                                        input: { min: 0, step: 0.1 },
                                    }}
                                    sx={{
                                        borderRadius: "4px",
                                        borderTopLeftRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderBottomLeftRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderLeft: {
                                            xs: undefined,
                                            sm: 0,
                                        },
                                        minWidth: 0,
                                    }}
                                />
                            </Box>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                disabled={disabled}
                                onClick={() => remove(index)}
                                sx={{
                                    display: { xs: "none", sm: "flex" },
                                    mt: 0.5,
                                    ml: 1,
                                }}
                            >
                                <X size={20} />
                            </IconButton>
                        </Box>
                    </Box>
                ))}

                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={16} />}
                    onClick={() => append({ Name: "", Pitch: "", Squares: "" })}
                    disabled={disabled}
                    sx={{ alignSelf: "flex-start", mt: 1 }}
                >
                    Add Roof Pitch
                </Button>
            </Stack>
        </Box>
    );
}
